import { useState, useMemo, useEffect } from "react";
import { Check, Filter, Search, Trash2, Upload } from "lucide-react";
import DatePicker from "react-datepicker";
import { format } from "date-fns";
import "react-datepicker/dist/react-datepicker.css";
import DashboardLayout from "../../../components/dashboard-layout/DashboardLayout";
import styles from "../../../styles/__lab_appointment_detail.module.scss";
import logo from "../../../assets/images/logo.png";
import user from "../../../assets/images/user.png";
import {
  composeBookingChildName,
  getBookingChildDob,
  getBookingChildGender,
  ageFromDob,
  titleCaseGender,
} from "../../../utils/childBooking";
import { IoMdSettings } from "react-icons/io";
import DialogReschedule from "../../../components/dialog-reschedule/DialogReschedule";
import DialogCreateFollowUp from "../../../components/dialog-create/DialogCreateFollowUp";
import DialogManageAvailability from "../../../components/dialog-manage-availability/DialogManageAvailability";
import DialogPatientRegistration from "../../../components/patient-registration/DialogPatientRegistration";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import AppointmentDetails from "../../../components/appointmentDetails/AppointmentDetails";
import Loader from "../../../components/loader/Loader";
import { RxChevronDown, RxClock } from "react-icons/rx";
import { FiDownload } from "react-icons/fi";
import {
  FaArrowLeftLong,
  FaCirclePlus,
  FaLocationArrow,
  FaPlus,
} from "react-icons/fa6";
import IconFileLargeView from "../../../assets/images/file-large-view.svg";
import reportPreview from "../../../assets/images/reportPreview.png";
import IconPDF from "../../../assets/images/IconPDF.png";
import UserAvatar from "../../../assets/images/patientAvatar.png";
import { BsFillChatTextFill } from "react-icons/bs";
import { Link, useParams, useNavigate } from "react-router-dom";
import { Button } from "bootstrap";
import { LiaMapMarkerSolid } from "react-icons/lia";
import Chat from "../../../components/chat/ChatModal";
import { IoIosCheckbox } from "react-icons/io";
import { AiOutlineFilePdf } from "react-icons/ai";
import LabDashboardLayout from "../../../components/labmod-dashboard-layout/LabModDashboardLayout";
import axios from "axios";
import { APP_PATH } from "../../../api/backendPath";
import { toast } from "../../../components/toast";
import {
  getLabOrderDetails,
  generateLabOrderInvoice,
  addLabOrderTests,
  scheduleLabOrderAppointment,
  getAvailableTechnicians,
  assignLabOrderTechnician,
  sendLabOrderConfirmation,
  getLabServiceFeesConfiguration,
} from "../../../services/labOrdersService";
import { getLabServices } from "../../../services/labTestsService";
import { getReportDetails } from "../../../services/labReportsService";
import { formatCustom as tzCustom, formatDateTimeNumeric as tzDateTimeNumeric } from "../../../utils/datetime";
import { formatPercent, formatWholeAmount } from "../../../utils/amountFormat";

const LabManagementPatientAppointmentDetail = ({
  onLogout,
  onNavigateToDashboard,
}) => {
  const { t, i18n } = useTranslation();
  const { id: urlOrderId } = useParams(); // Get order ID from URL params
  const navigate = useNavigate(); // Add navigation hook

  const [showDetails, setShowDetails] = useState(false);
  const [activeTab, setActiveTab] = useState("Order Details");
  const [currentPage, setCurrentPage] = useState(1);
  const [sortField, setSortField] = useState("name");
  const [sortDirection, setSortDirection] = useState("asc");
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("All");
  const [dateFilter, setDateFilter] = useState("");
  const itemsPerPage = 10;
  const [date, setDate] = useState(null);

  // Service date and time state
  const [serviceDate, setServiceDate] = useState(null);
  const [serviceTime, setServiceTime] = useState("");
  const [openDialogReschedule, setOpenDialogReschedule] = useState(false);
  const [openDialogCreateFollowUp, setOpenDialogCreateFollowUp] =
    useState(false);
  const [openDialogManageAvailability, setOpenDialogManageAvailability] =
    useState(false);
  const [openDialogPatientRegistration, setOpenDialogPatientRegistration] =
    useState(false);
  const [gender, setGender] = useState("");

  // Order details state
  const [orderDetails, setOrderDetails] = useState(null);
  // The child's composed name on a child booking, empty otherwise. Drives the header split (child
  // as the patient, account holder as the guardian); empty also when the snapshot has no name, so
  // the header then keeps the account holder instead of going blank.
  const childHeaderName = composeBookingChildName(orderDetails);
  const [loadingOrderDetails, setLoadingOrderDetails] = useState(false);

  // Admin fee percentage — ALWAYS from the admin panel configuration
  // (GET /api/v1/lab/orders/fees/configuration), never a hardcoded rate. Default 0: no
  // admin fee is applied unless the admin panel has an ACTIVE admin fee configured for the
  // lab service. Used only as the fallback for orders that carry no persisted fee snapshot
  // (`invoice.admin_fee_percentage`), which remains the source of truth for a priced order.
  // Named `config...` deliberately: the invoice breakdown below declares its own local
  // `adminFeePercentage`, so a matching name here would be shadowed and silently unused.
  const [configAdminFeePercentage, setConfigAdminFeePercentage] = useState(0);

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const result = await getLabServiceFeesConfiguration();
        if (!active) return;
        const cfg = result?.data?.admin_fee;
        // Respect the is_active flag: an inactive configuration means no admin fee.
        if (result?.status && cfg?.is_active && cfg?.percentage != null) {
          setConfigAdminFeePercentage(parseFloat(cfg.percentage) || 0);
        } else {
          setConfigAdminFeePercentage(0);
        }
      } catch (e) {
        // Never invent a percentage the platform never configured — charge nothing.
        if (active) setConfigAdminFeePercentage(0);
        console.warn(
          "Failed to fetch lab admin fee configuration; applying 0%:",
          e?.message,
        );
      }
    })();
    return () => {
      active = false;
    };
  }, []);

  // Report details state
  const [reportDetails, setReportDetails] = useState(null);
  const [loadingReportDetails, setLoadingReportDetails] = useState(false);

  // Test management state
  const [tests, setTests] = useState([]);
  const [currentTest, setCurrentTest] = useState({
    testName: "",
    serviceCode: "",
    price: "",
    location: "ON_SITE",
    transportationFee: "0",
  });
  const [isSavingTests, setIsSavingTests] = useState(false);

  // Lab services state
  const [labServices, setLabServices] = useState([]);
  const [loadingLabServices, setLoadingLabServices] = useState(false);
  const [selectedTestId, setSelectedTestId] = useState("");
  const [showAddTestRow, setShowAddTestRow] = useState(false);

  // Edit test state
  const [editingTestId, setEditingTestId] = useState(null);
  const [editingTest, setEditingTest] = useState({
    testName: "",
    serviceCode: "",
    price: "",
    location: "ON_SITE",
    transportationFee: "0",
  });
  const [editingTestServiceId, setEditingTestServiceId] = useState("");

  // Technician management state
  const [availableTechnicians, setAvailableTechnicians] = useState([]);
  const [loadingTechnicians, setLoadingTechnicians] = useState(false);
  const [selectedTechnicianId, setSelectedTechnicianId] = useState("");

  // Location conflict dialog state
  const [showLocationConflictDialog, setShowLocationConflictDialog] =
    useState(false);
  const [conflictingTest, setConflictingTest] = useState(null);

  // File upload state
  const [uploadingFile, setUploadingFile] = useState(false);
  const [uploadedFiles, setUploadedFiles] = useState([]);

  // Use urlOrderId directly from useParams instead of storing in state
  const orderId = urlOrderId;

  // Share results with patient
  const handleShareWithPatient = async () => {
    if (!orderId) {
      toast.error("No order ID available");
      return;
    }

    // Check if report is already sent
    if (orderDetails?.reportStatus === "SENT") {
      toast.info("Report has already been shared with the patient");
      return;
    }

    try {
      console.log("Sharing results with patient for order:", orderId);

      // Create axios instance with base URL and auth token
      const token = sessionStorage.getItem("usr_1a2b3c");
      const axiosInstance = axios.create({
        baseURL: process.env.REACT_APP_API_URL,
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });

      // Call the resend report endpoint to share results with patient
      const response = await axiosInstance.post(
        `${APP_PATH.resendReport}/${orderId}/resend`,
        {},
      );

      if (response.data.status) {
        toast.success("Results shared with patient successfully");
        // Refresh order details to get updated report status
        fetchOrderDetails();
      } else {
        toast.error(
          response.data.message || "Failed to share results with patient",
        );
      }
    } catch (error) {
      console.error("Error sharing results with patient:", error);
      if (error.response?.data?.message) {
        toast.error(error.response.data.message);
      } else {
        toast.error("Failed to share results with patient");
      }
    }
  };

  // Handle multiple file upload for test results
  const handleMultipleFileUpload = async (event) => {
    const files = Array.from(event.target.files);
    if (!files || files.length === 0) return;

    // Check if report is already sent
    if (orderDetails?.reportStatus === "SENT") {
      toast.info(
        "Report has already been shared with the patient. Upload is disabled.",
      );
      event.target.value = ""; // Clear the file input
      return;
    }

    // Validate file count (max 10 files)
    if (files.length > 10) {
      toast.error("Maximum 10 files allowed at once");
      return;
    }

    // Validate each file
    for (const file of files) {
      // Validate file type (only PDF)
      if (file.type !== "application/pdf") {
        toast.error(
          `Only PDF files are allowed. "${file.name}" is not a PDF file.`,
        );
        return;
      }

      // Validate file size (25MB max)
      const maxSize = 25 * 1024 * 1024; // 25MB
      if (file.size > maxSize) {
        toast.error(`File "${file.name}" is too large. Maximum size is 25MB.`);
        return;
      }
    }

    if (!orderId) {
      toast.error("No order ID available");
      return;
    }

    // Handle single file upload
    if (files.length === 1) {
      const file = files[0];
      setUploadingFile(true);

      try {
        console.log("Uploading single test results file for order:", orderId);

        // Create FormData for file upload
        const formData = new FormData();
        formData.append("file", file);

        // Create axios instance with base URL and auth token
        const token = sessionStorage.getItem("usr_1a2b3c");
        const axiosInstance = axios.create({
          baseURL: process.env.REACT_APP_API_URL,
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "multipart/form-data",
          },
        });

        // Choose endpoint based on file size
        const fileSizeMB = file.size / (1024 * 1024);
        const isLargeFile = fileSizeMB > 10;
        const endpoint = isLargeFile
          ? `${APP_PATH.uploadResultsMultipart}/${orderId}/upload-results-multipart`
          : `${APP_PATH.uploadResults}/${orderId}/upload-results`;

        console.log(
          `File size: ${fileSizeMB.toFixed(2)}MB, Using ${isLargeFile ? "multipart" : "regular"} upload`,
        );

        if (isLargeFile) {
          toast.info(
            `Uploading large file (${fileSizeMB.toFixed(1)}MB). This may take a few minutes...`,
          );
        }

        // Upload file using the appropriate endpoint
        const response = await axiosInstance.post(endpoint, formData);

        if (response.data.status) {
          toast.success("Test results uploaded successfully");
          // Refresh order details to get updated status
          await fetchOrderDetails();
        } else {
          toast.error(response.data.message || "Failed to upload test results");
        }
      } catch (error) {
        console.error("Error uploading test results:", error);
        if (error.response?.data?.message) {
          toast.error(error.response.data.message);
        } else {
          toast.error("Failed to upload test results");
        }
      } finally {
        setUploadingFile(false);
        // Clear the file input
        event.target.value = "";
      }
      return;
    }

    // Handle multiple files upload
    setUploadingFile(true);

    try {
      console.log(
        `Uploading ${files.length} test results files for order:`,
        orderId,
      );

      // Create FormData for multiple file upload
      const formData = new FormData();
      files.forEach((file, index) => {
        formData.append("files", file);
      });

      // Create axios instance with base URL and auth token
      const token = sessionStorage.getItem("usr_1a2b3c");
      const axiosInstance = axios.create({
        baseURL: process.env.REACT_APP_API_URL,
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "multipart/form-data",
        },
      });

      const endpoint = `${APP_PATH.uploadMultipleResults}/${orderId}/upload-multiple-results`;

      console.log(
        `Uploading ${files.length} files using multi-file upload endpoint`,
      );

      toast.info(
        `Uploading ${files.length} files. This may take a few minutes...`,
      );

      // Upload files using the multiple upload endpoint
      const response = await axiosInstance.post(endpoint, formData);

      if (response.data.status) {
        toast.success(`${files.length} test results uploaded successfully`);
        // Refresh order details to get updated status
        await fetchOrderDetails();
      } else {
        toast.error(response.data.message || "Failed to upload test results");
      }
    } catch (error) {
      console.error("Error uploading multiple test results:", error);
      if (error.response?.data?.message) {
        toast.error(error.response.data.message);
      } else {
        toast.error("Failed to upload test results");
      }
    } finally {
      setUploadingFile(false);
      // Clear the file input
      event.target.value = "";
    }
  };

  // Handle file click to open in new tab
  const handleFileClick = async (fileId, fileName) => {
    try {
      const token = sessionStorage.getItem("usr_1a2b3c");
      if (!token) {
        toast.error("Authentication token not found");
        return;
      }

      // Show loading state
      toast.info("Opening file...");

      console.log("Attempting to open file:", {
        fileId,
        fileName,
        orderId: urlOrderId,
      });

      const response = await fetch(
        `${process.env.REACT_APP_API_URL}${APP_PATH.getFilePresignedUrl}/${urlOrderId}/file/${fileId}/presigned-url`,
        {
          method: "GET",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        },
      );

      const result = await response.json();
      console.log("Presigned URL response:", result);

      if (result.status && result.data?.presignedUrl) {
        // Open file in new tab
        window.open(result.data.presignedUrl, "_blank");
      } else {
        console.error("Presigned URL error:", result);
        toast.error(
          result.message ||
            "Failed to open file. The file may not be available or you may not have permission to access it.",
        );
      }
    } catch (error) {
      console.error("Error opening file:", error);
      toast.error(
        "Failed to open file. Please try again or contact support if the issue persists.",
      );
    }
  };

  // Handle file deletion
  const handleFileDelete = async (fileId, fileName) => {
    // Check if report is already sent
    if (orderDetails?.reportStatus === "SENT") {
      toast.info(
        "Report has already been shared with the patient. File deletion is disabled.",
      );
      return;
    }

    try {
      const token = sessionStorage.getItem("usr_1a2b3c");
      if (!token) {
        toast.error("Authentication token not found");
        return;
      }

      // Show confirmation dialog
      if (!window.confirm(`Are you sure you want to delete "${fileName}"?`)) {
        return;
      }

      console.log("Attempting to delete file:", {
        fileId,
        fileName,
        orderId: urlOrderId,
      });

      const response = await fetch(
        `${process.env.REACT_APP_API_URL}${APP_PATH.deleteFile}/${urlOrderId}/file/${fileId}`,
        {
          method: "DELETE",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        },
      );

      const result = await response.json();
      console.log("Delete file response:", result);

      if (result.status) {
        toast.success("File deleted successfully");
        // Refresh order details to get updated file list
        await fetchOrderDetails();
      } else {
        console.error("Delete file error:", result);
        toast.error(
          result.message ||
            "Failed to delete file. You may not have permission to delete this file.",
        );
      }
    } catch (error) {
      console.error("Error deleting file:", error);
      toast.error(
        "Failed to delete file. Please try again or contact support if the issue persists.",
      );
    }
  };

  // Get time range based on preferred time slot
  const getTimeRangeForSlot = (timeSlot) => {
    if (!timeSlot) return { min: "00:00", max: "23:59" };

    const slot = timeSlot.toUpperCase();

    if (slot.includes("MORNING")) {
      return { min: "08:00", max: "12:00" };
    } else if (slot.includes("AFTERNOON")) {
      return { min: "12:00", max: "17:00" };
    } else if (slot.includes("EVENING")) {
      return { min: "17:00", max: "19:30" };
    }

    return { min: "00:00", max: "23:59" };
  };

  // Generate time options based on time slot
  const generateTimeOptions = (timeSlot) => {
    const timeRange = getTimeRangeForSlot(timeSlot);
    const options = [];

    // Parse min and max times
    const [minHour, minMinute] = timeRange.min.split(":").map(Number);
    const [maxHour, maxMinute] = timeRange.max.split(":").map(Number);

    // Generate time options in 30-minute intervals
    for (let hour = minHour; hour <= maxHour; hour++) {
      for (let minute = 0; minute < 60; minute += 30) {
        // Skip if before min time
        if (hour === minHour && minute < minMinute) continue;

        // Skip if after max time
        if (hour === maxHour && minute > maxMinute) break;

        // Skip if hour is past max hour
        if (hour > maxHour) break;

        const timeValue = `${String(hour).padStart(2, "0")}:${String(minute).padStart(2, "0")}`;
        const displayHour = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour;
        const period = hour >= 12 ? "PM" : "AM";
        const displayTime = `${displayHour}:${String(minute).padStart(2, "0")} ${period}`;

        options.push({ value: timeValue, label: displayTime });
      }
    }

    return options;
  };

  // Get current time range based on order's preferred time slot
  const timeRange = getTimeRangeForSlot(orderDetails?.preferredTimeSlot);
  const timeOptions = generateTimeOptions(orderDetails?.preferredTimeSlot);

  // Manual test function
  const testFetchOrderDetails = () => {
    console.log("Manual test button clicked");
    console.log("Will fetch order with ID:", orderId);
    if (orderId) {
      fetchOrderDetails();
    } else {
      console.error("No orderId available!");
      toast.error("No order ID found in URL");
    }
  };

  const calculateInvoiceTotal = () => {
    if (!tests || tests.length === 0) {
      return {
        subtotal: 0,
        transportationFees: 0,
        adminFee: 0,
        total: 0,
      };
    }

    const subtotal = tests.reduce((sum, test) => {
      const price = parsePrice(test.price);
      return sum + price;
    }, 0);

    const transportationFees = tests.reduce((sum, test) => {
      const transportationFee = parsePrice(test.transportationFee);
      return sum + transportationFee;
    }, 0);

    // Admin fee percentage: the order's persisted snapshot when it has one (a priced order
    // is a historical record), otherwise the LIVE admin panel configuration. Never a
    // hardcoded rate. `??` not `||` so a legitimate 0 snapshot is respected instead of
    // falling through to the live percentage.
    const effectiveAdminFeePercentage = Number(
      orderDetails?.invoice?.admin_fee_percentage ??
        configAdminFeePercentage ??
        0,
    );
    // Admin fee is calculated on (subtotal + transportation fee), matching the backend invoice.
    const adminFee =
      (subtotal + transportationFees) * (effectiveAdminFeePercentage / 100);
    const total = subtotal + transportationFees + adminFee;

    return {
      subtotal,
      transportationFees,
      adminFee,
      total,
      admin_fee_percentage: effectiveAdminFeePercentage,
    };
  };

  // Manual invoice calculation test
  const testInvoiceCalculation = () => {
    console.log("=== MANUAL INVOICE CALCULATION TEST ===");
    const result = calculateInvoiceTotal();
    console.log("Manual calculation result:", result);
    toast.info(`Total: IQD ${formatWholeAmount(result.total)}`);
  };

  const dispatch = useDispatch();
  const {
    data: apiAppointments,
    isLoading: loading,
    error,
    totalPages: reduxTotalPages,
  } = useSelector((state) => state.fetchDoctorAppointments);

  const [isChatOpen, setIsChatOpen] = useState(false);
  const toggleChat = () => {
    setIsChatOpen((prev) => !prev);
  };

  // Use Redux state for totalPages, fallback to local state if needed
  const totalPages = reduxTotalPages || 1;
  const today = new Date();
  const formatted = format(today, "MMMM dd, yyyy");
  const [selectedAppointment, setSelectedAppointment] = useState(null);

  // Fetch order details on component mount or when orderId changes
  useEffect(() => {
    console.log(
      "LabManagementPatientAppointmentDetail mounted, orderId:",
      orderId,
    );
    if (orderId) {
      console.log("Fetching order details for orderId:", orderId);
      fetchOrderDetails();
      fetchReportDetails(); // Also fetch report details for report status
    } else {
      console.warn("No orderId available to fetch order details");
    }

    // Fetch lab services for dropdown
    fetchLabServices();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderId]);

  // Test management functions
  const handleTestInputChange = (field, value) => {
    console.log(
      `handleTestInputChange - Field: ${field}, Value: ${value}, Type: ${typeof value}`,
    );
    setCurrentTest((prev) => {
      const updated = {
        ...prev,
        [field]: value,
      };
      console.log("Updated currentTest:", updated);
      return updated;
    });
  };

  // Fetch lab services for dropdown
  const fetchLabServices = async (page = 1, limit = 100) => {
    setLoadingLabServices(true);
    try {
      const response = await getLabServices({ page, limit });

      console.log("Lab services API response:", response);

      if (response?.status) {
        const servicesData = response.data?.services || [];
        setLabServices(servicesData);

        if (servicesData.length === 0) {
          // Silent — the dropdown's empty state is enough.
        }
      } else {
        setLabServices([]);
        toast.error(response?.message || "Failed to fetch lab services");
      }
    } catch (error) {
      console.error("Error fetching lab services:", error);
      setLabServices([]);
      const errorMessage =
        error.response?.data?.message ||
        error.message ||
        "Failed to fetch lab services";
      toast.error(errorMessage);
    } finally {
      setLoadingLabServices(false);
    }
  };

  // Fetch order details
  const fetchOrderDetails = async () => {
    console.log("=== fetchOrderDetails START ===");
    console.log("orderId:", orderId);

    if (!orderId) {
      console.warn("No order ID provided");
      toast.warning("No order ID found in URL");
      return;
    }

    setLoadingOrderDetails(true);

    try {
      const response = await getLabOrderDetails(orderId);
      console.log("Order details response:", response);

      if (response && response.status) {
        setOrderDetails(response.data);
        console.log("Order details set successfully:", response.data);
        console.log("Report status fields check:");
        console.log("- reportStatus:", response.data.reportStatus);
        console.log("- report_status:", response.data.report_status);
        console.log("- status:", response.data.status);
        console.log("- All keys:", Object.keys(response.data));

        // Set gender based on preferred technician gender
        if (response.data.preferredTechnicianGender) {
          const preferredGender =
            response.data.preferredTechnicianGender.toLowerCase();
          if (preferredGender === "male" || preferredGender === "female") {
            setGender(preferredGender);
          }
        }

        // If tests exist in order details, populate the tests list
        if (response.data.tests && response.data.tests.length > 0) {
          const formattedTests = response.data.tests.map((test) => ({
            id: test.id || Date.now() + Math.random(),
            testName: test.test_name || test.testName,
            serviceCode: test.service_code || test.serviceCode,
            price: test.price,
            location: test.location,
            transportationFee: test.transportation_fee || test.transportationFee || "0",
          }));

          setTests(formattedTests);
        }

        // Populate service date and time if available
        if (response.data.scheduledDate) {
          setServiceDate(new Date(response.data.scheduledDate));
        }
        if (response.data.scheduledTime) {
          setServiceTime(response.data.scheduledTime);
        }
      } else {
        console.error("Failed response:", response);
        toast.error(response?.message || "Failed to fetch order details");
      }
    } catch (error) {
      console.error("Error fetching order details:", error);
      const errorMessage =
        error.response?.data?.message ||
        error.message ||
        "Failed to fetch order details";
      toast.error(errorMessage);
    } finally {
      setLoadingOrderDetails(false);
    }
  };

  // Fetch report details to get report status
  const fetchReportDetails = async () => {
    console.log("=== fetchReportDetails START ===");
    console.log("orderId:", orderId);

    if (!orderId) {
      console.warn("No order ID provided for report details");
      return;
    }

    setLoadingReportDetails(true);

    try {
      const response = await getReportDetails(orderId);
      console.log("Report details response:", response);

      if (response && response.status) {
        setReportDetails(response.data);
        console.log("Report details set successfully:", response.data);
        console.log(
          "Report status from reports API:",
          response.data.reportStatus ||
            response.data.report_status ||
            response.data.status,
        );
      } else {
        console.error("Failed report details response:", response);
      }
    } catch (error) {
      console.error("Error fetching report details:", error);
      // Don't show error toast as this is supplementary data
    } finally {
      setLoadingReportDetails(false);
      console.log("=== fetchReportDetails END ===");
    }
  };

  // Helper function to safely parse price values
  const parsePrice = (priceValue) => {
    if (!priceValue) return 0;
    const cleanPrice = String(priceValue).replace(/,/g, "");
    const parsed = parseFloat(cleanPrice);
    return isNaN(parsed) ? 0 : parsed;
  };

  // Handle navigation
  const handleNavigate = (page) => {
    if (page === "dashboard") {
      onNavigateToDashboard();
    }
  };

  // Format status text for display
  const formatStatusText = (status) => {
    if (!status) return "";
    const statusDisplayMap = {
      REQUEST_SUBMITTED: "RECEIVED",
      request_submitted: "RECEIVED",
    };

    const lowerStatus = status.toLowerCase();
    if (statusDisplayMap[lowerStatus]) {
      return statusDisplayMap[lowerStatus];
    }

    if (statusDisplayMap[status]) {
      return statusDisplayMap[status];
    }

    return status
      .split(/[_\s-]+/)
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(" ");
  };

  // Define tabs based on order status
  const tabs = (() => {
    const allTabs = ["Order Details", "Technician Detail", "Results & Reports"];
    const orderStatus = orderDetails?.status;

    const earlyStatuses = [
      "REQUEST_SUBMITTED",
      "AWAITING_CONFIRMATION",
      "AWAITING_PAYMENT",
      "INVOICE_RECEIVED",
    ];

    if (earlyStatuses.includes(orderStatus)) {
      return ["Order Details"];
    }

    if (orderStatus === "COMPLETED") {
      return ["Order Details", "Results & Reports"];
    }

    return allTabs;
  })();

  const getStatusColor = (status) => {
    switch (status) {
      case "Completed":
        return "status-completed";
      case "Cancelled":
        return "status-cancelled";
      case "Reschedule Requested":
        return "status-reschedule";
      case "Pending":
        return "status-pending";
      case "COD":
        return "status-cod";
      case "Payment Complete":
        return "status-payment";
      case "Lab":
        return "status-labtype";
      case "Imaging":
        return "status-imagingtype";
      case "Urgent":
        return "status-red";
      default:
        return "";
    }
  };

  const EditIcon = () => (
    <svg
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        fill-rule="evenodd"
        clip-rule="evenodd"
        d="M11.9525 3.04199H13.25C13.4157 3.04199 13.5747 3.10784 13.6919 3.22505C13.8091 3.34226 13.875 3.50123 13.875 3.66699C13.875 3.83275 13.8091 3.99172 13.6919 4.10893C13.5747 4.22614 13.4157 4.29199 13.25 4.29199H12C10.0183 4.29199 8.59496 4.29366 7.51163 4.43866C6.44579 4.58199 5.80329 4.85533 5.32829 5.32949C4.85413 5.80449 4.58163 6.44616 4.43829 7.51199C4.29329 8.59532 4.29163 10.0187 4.29163 12.0003C4.29163 13.982 4.29329 15.4053 4.43829 16.4887C4.58163 17.5545 4.85496 18.197 5.32913 18.672C5.80413 19.1462 6.44579 19.4187 7.51163 19.562C8.59496 19.707 10.0183 19.7087 12 19.7087C13.9816 19.7087 15.405 19.707 16.4883 19.562C17.5541 19.4187 18.1966 19.1453 18.6716 18.6712C19.1458 18.1962 19.4183 17.5545 19.5616 16.4887C19.7066 15.4053 19.7083 13.982 19.7083 12.0003V10.7503C19.7083 10.5846 19.7741 10.4256 19.8913 10.3084C20.0086 10.1912 20.1675 10.1253 20.3333 10.1253C20.4991 10.1253 20.658 10.1912 20.7752 10.3084C20.8924 10.4256 20.9583 10.5846 20.9583 10.7503V12.0478C20.9583 13.972 20.9583 15.4795 20.8 16.6562C20.6383 17.8595 20.3 18.8095 19.555 19.5553C18.8091 20.3012 17.8591 20.6387 16.655 20.8003C15.4791 20.9587 13.9716 20.9587 12.0475 20.9587H11.9525C10.0283 20.9587 8.52079 20.9587 7.34413 20.8003C6.14079 20.6387 5.19079 20.3003 4.44496 19.5553C3.69913 18.8095 3.36163 17.8595 3.19996 16.6553C3.04163 15.4795 3.04163 13.972 3.04163 12.0478V11.9528C3.04163 10.0287 3.04163 8.52116 3.19996 7.34449C3.36163 6.14116 3.69996 5.19116 4.44496 4.44533C5.19079 3.69949 6.14079 3.36199 7.34496 3.20033C8.52079 3.04199 10.0283 3.04199 11.9525 3.04199ZM15.975 3.89699C16.5249 3.3622 17.2633 3.06536 18.0303 3.07068C18.7974 3.076 19.5316 3.38304 20.074 3.92541C20.6165 4.46777 20.9237 5.20187 20.9292 5.96895C20.9347 6.73603 20.638 7.47444 20.1033 8.02449L14.5633 13.5653C14.2541 13.8737 14.06 14.0687 13.8441 14.237C13.5894 14.436 13.3136 14.6065 13.0216 14.7453C12.7741 14.8628 12.5133 14.9495 12.0991 15.0878L9.67829 15.8945C9.45927 15.9674 9.22427 15.978 8.99961 15.9249C8.77494 15.8718 8.56949 15.7573 8.40626 15.594C8.24302 15.4308 8.12846 15.2253 8.07539 15.0007C8.02232 14.776 8.03285 14.541 8.10579 14.322L8.91246 11.9012C9.05079 11.4862 9.13746 11.2262 9.25496 10.9787C9.39496 10.6864 9.5644 10.4123 9.76329 10.1562C9.93163 9.93949 10.1258 9.74616 10.435 9.43699L15.975 3.89699ZM19.2191 4.78033C18.9061 4.46747 18.4817 4.29172 18.0391 4.29172C17.5966 4.29172 17.1721 4.46747 16.8591 4.78033L16.5458 5.09449C16.5636 5.17505 16.5908 5.26894 16.6275 5.37616C16.7466 5.72033 16.9733 6.17366 17.4 6.60033C17.8154 7.01596 18.3359 7.31115 18.9058 7.45449L19.2191 7.14116C19.532 6.82815 19.7077 6.40371 19.7077 5.96116C19.7077 5.5186 19.532 5.09333 19.2191 4.78033ZM17.9208 8.44033C17.3961 8.21408 16.9192 7.88998 16.5158 7.48533C16.1111 7.08187 15.787 6.60504 15.5608 6.08033L11.3483 10.292C11.0008 10.6395 10.865 10.777 10.7483 10.9253C10.6066 11.1093 10.4846 11.3076 10.3841 11.517C10.3025 11.6862 10.2408 11.8695 10.085 12.3353L9.72579 13.4145L10.5858 14.2753L11.665 13.9153C12.1316 13.7595 12.3141 13.6978 12.4841 13.617C12.6941 13.517 12.8913 13.395 13.0758 13.2512C13.2233 13.1353 13.3608 12.9995 13.7083 12.6528L17.9208 8.44033Z"
        fill="#053345"
      />
    </svg>
  );

  return (
    <LabDashboardLayout
      currentPage="lab_report-management"
      onNavigate={handleNavigate}
      onLogout={onLogout}
    >
      <div className={styles["page-header"]}>
        <FaArrowLeftLong
          onClick={() => navigate(-1)}
          style={{ cursor: "pointer" }}
        />
        <h2>Appointment Detail</h2>
      </div>
      <div className={styles["apt-appointments-section"]}>
        <div className={`${styles["apt-section-header"]} mb-2`}>
          <h2>
            Appointment ID:{" "}
            <b>
              {orderDetails?.bookingNumber || orderDetails?.id || "Loading..."}
            </b>{" "}
            {orderDetails?.status && (
              <span className={styles["tag-received"]}>
                {formatStatusText(orderDetails.status)}
              </span>
            )}{" "}
            {orderDetails?.isUrgent ? (
              <span className={styles["tag-urgent"]}>Urgent</span>
            ) : (
              <span className={styles["tag-noturgent"]}>Not Urgent</span>
            )}
          </h2>
          <div className={styles["apt-top-actions"]}>
            {/* {activeTab === "Order Details" && (
                <button className={styles["exportBtn"]}>
                  <FiDownload /> Export
                </button>
                )}
                
                {activeTab === "Technician Detail" && (<>
                  <button className={styles["chat-icon-btn"]} onClick={toggleChat}>
                    <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path fillRule="evenodd" clipRule="evenodd" d="M10 20C15.523 20 20 15.523 20 10C20 4.477 15.523 0 10 0C4.477 0 0 4.477 0 10C0 11.6 0.376 13.112 1.043 14.453C1.221 14.809 1.28 15.216 1.177 15.601L0.582 17.827C0.52307 18.0473 0.523124 18.2792 0.582155 18.4995C0.641186 18.7197 0.757118 18.9206 0.918315 19.0819C1.07951 19.2432 1.2803 19.3593 1.50053 19.4184C1.72075 19.4776 1.95267 19.4778 2.173 19.419L4.399 18.823C4.78538 18.7254 5.19414 18.7727 5.548 18.956C6.93095 19.6446 8.45512 20.002 10 20ZM6 11.25C5.80109 11.25 5.61032 11.329 5.46967 11.4697C5.32902 11.6103 5.25 11.8011 5.25 12C5.25 12.1989 5.32902 12.3897 5.46967 12.5303C5.61032 12.671 5.80109 12.75 6 12.75H11.5C11.6989 12.75 11.8897 12.671 12.0303 12.5303C12.171 12.3897 12.25 12.1989 12.25 12C12.25 11.8011 12.171 11.6103 12.0303 11.4697C11.8897 11.329 11.6989 11.25 11.5 11.25H6ZM5.25 8.5C5.25 8.30109 5.32902 8.11032 5.46967 7.96967C5.61032 7.82902 5.80109 7.75 6 7.75H14C14.1989 7.75 14.3897 7.82902 14.5303 7.96967C14.671 8.11032 14.75 8.30109 14.75 8.5C14.75 8.69891 14.671 8.88968 14.5303 9.03033C14.3897 9.17098 14.1989 9.25 14 9.25H6C5.80109 9.25 5.61032 9.17098 5.46967 9.03033C5.32902 8.88968 5.25 8.69891 5.25 8.5Z" fill="#2B7FFF"/>
                    </svg>
                  </button>

                  {isChatOpen && <Chat />}
                </>)} */}
          </div>
        </div>

        <div className={styles["patient-appointment-tabs"]}>
          {tabs.map((tab) => (
            <button
              key={tab}
              className={`${styles["patient-tab"]} ${
                activeTab === tab ? styles["active"] : ""
              }`}
              onClick={() => setActiveTab(tab)}
            >
              {tab}
            </button>
          ))}
        </div>

        {activeTab === "Order Details" && (
          <div className={styles["patient-tab-content"]}>
            {loading ? (
              <div className="text-center py-5">
                <Loader />
              </div>
            ) : error ? (
              <div className="text-center py-5">
                <div className="alert alert-danger">
                  <h4>Error Loading Report Details</h4>
                  <p>{error}</p>
                  <button
                    className="btn btn-primary mt-2"
                    onClick={fetchOrderDetails}
                  >
                    Retry
                  </button>
                </div>
              </div>
            ) : orderDetails ? (
              <>
                <div className={styles["white-card"]}>
                  <div
                    className={`${styles["patient-tab-title"]} ${styles["med-tab-title"]}`}
                  >
                    <h2>Patient Information</h2>
                    <div className={styles["patient-actions-wrap"]}></div>
                  </div>

                  <div className={styles["field-block"]}>
                    <div className={`${styles["label"]} mb-2`}>
                      Patient Name
                    </div>
                    <div className={styles["user-section"]}>
                      <div className={styles["userInfo"]}>
                        {/* Child booking: the CHILD is the patient, so the header shows the
                            child's name + "Child" badge and the account holder moves to the
                            Guardian Name field below — same as the main order details page
                            and the doctor app's appointment details. No patient photo or
                            child icon is shown. */}
                        <h3>
                          {childHeaderName ? (
                            <>
                              {childHeaderName}
                              <span
                                style={{
                                  marginLeft: "8px",
                                  fontSize: "11px",
                                  background: "#0cbdbd",
                                  color: "#fff",
                                  padding: "2px 8px",
                                  borderRadius: "10px",
                                  fontWeight: 500,
                                  verticalAlign: "middle",
                                }}
                              >
                                {t("childBooking.childBadge", "Child")}
                              </span>
                            </>
                          ) : (
                            <>
                              {orderDetails?.patient?.full_name || ""}{" "}
                              {orderDetails?.patient?.surname || "N/A"}
                            </>
                          )}
                        </h3>
                      </div>

                      {/* <div className={styles["msgIcon"]}>
                              <BsFillChatTextFill />
                            </div> */}
                    </div>
                  </div>

                  <div className={styles["medical-history"]}>
                    <div
                      className={`${styles["history-grid"]} ${styles["med-grid"]} border-0 mb-0 pb-0`}
                    >
                      {/* Guardian — child bookings only: the account holder who booked
                          for the child, so the center still knows who to contact. */}
                      {childHeaderName && (
                        <div className={styles["field-block"]}>
                          <div className={styles["label"]}>
                            {t("childBooking.guardianName", "Guardian Name")}
                          </div>
                          <div className={styles["value"]}>
                            {[
                              orderDetails?.patient?.full_name,
                              orderDetails?.patient?.surname,
                            ]
                              .filter(Boolean)
                              .join(" ") || "N/A"}
                          </div>
                        </div>
                      )}
                      <div className={styles["field-block"]}>
                        <div className={styles["label"]}>Age | Gender</div>
                        <div className={styles["value"]}>
                          {/* Same person as the header: the child on a child booking. */}
                          {childHeaderName ? (
                            <>
                              {ageFromDob(getBookingChildDob(orderDetails)) ??
                                "N/A"}{" "}
                              yr |{" "}
                              {titleCaseGender(
                                getBookingChildGender(orderDetails),
                              ) || "N/A"}
                            </>
                          ) : (
                            <>
                              {orderDetails.patient?.age || "N/A"} yr |{" "}
                              {orderDetails.patient?.gender || "N/A"}
                            </>
                          )}
                        </div>
                      </div>

                      <div className={styles["field-block"]}>
                        <div className={styles["label"]}>Mobile Number</div>
                        <div className={styles["value"]}>
                          {orderDetails.patient?.mobile_number || "N/A"}
                        </div>
                      </div>

                      <div className={styles["field-block"]}>
                        <div className={styles["label"]}>
                          Preferred Technician Gender{" "}
                        </div>
                        <div className={styles["value"]}>
                          {orderDetails.preferredTechnicianGender || "Any"}
                        </div>
                      </div>

                      <div className={styles["field-block"]}>
                        <div className={styles["label"]}>Service Type</div>
                        <div className={styles["value"]}>
                          <span className={styles["badge-lab"]}>
                            {orderDetails.serviceType || "Lab"}
                          </span>
                        </div>
                      </div>

                      <div className={styles["field-block"]}>
                        <div className={styles["label"]}>
                          Preferred Location
                        </div>
                        <div className={styles["value"]}>
                          <span
                            className={
                              orderDetails?.preferredLocation === "Visit Center"
                                ? styles["badge-onsite"]
                                : styles["badge-lab"]
                            }
                          >
                            {orderDetails?.preferredLocation === "Visit Center"
                              ? "On-Site"
                              : orderDetails?.preferredLocation || "N/A"}
                          </span>
                        </div>
                      </div>

                      <div className={styles["field-block"]}>
                        <div className={styles["label"]}>Preferred Date</div>
                        <div className={styles["value"]}>
                          {orderDetails.preferredDate
                            ? tzCustom(new Date(orderDetails.preferredDate), {
                                  day: "2-digit",
                                  month: "short",
                                  year: "numeric",
                                })
                                .toUpperCase()
                            : "N/A"}
                        </div>
                      </div>

                      <div className={styles["field-block"]}>
                        <div className={styles["label"]}>
                          Preferred Time Slot
                        </div>
                        <div className={styles["value"]}>
                          {orderDetails.preferredTimeSlot || "N/A"}
                        </div>
                      </div>

                      <div className={styles["field-block"]}>
                        <div className={styles["label"]}>Payment Method</div>
                        <div className={styles["value"]}>
                          {orderDetails?.paymentMethod ? (
                            <span className={styles["badge-cod"]}>
                              {orderDetails.paymentMethod}
                            </span>
                          ) : (
                            "N/A"
                          )}
                        </div>
                      </div>

                      <div className={styles["field-block"]}>
                        <div className={styles["label"]}>Insurance Status</div>
                        <div
                          className={`${styles["value"]} ${styles["status-payment"]} bg-transparent`}
                        >
                          {orderDetails.insuranceStatus || "N/A"}
                        </div>
                      </div>

                      <div className={styles["field-block"]}>
                        <div className={styles["label"]}>Report Status</div>
                        <div className={styles["value"]}>
                          <span
                            className={
                              reportDetails?.reportStatus === "SENT" ||
                              reportDetails?.report_status === "SENT" ||
                              reportDetails?.status === "REPORT_SENT" ||
                              orderDetails?.reportStatus === "SENT" ||
                              orderDetails?.report_status === "SENT" ||
                              orderDetails?.status === "REPORT_SENT"
                                ? styles["reportstatus-Sent"]
                                : styles["reportstatus-NotSent"]
                            }
                          >
                            {reportDetails?.reportStatus === "SENT" ||
                            reportDetails?.report_status === "SENT" ||
                            reportDetails?.status === "REPORT_SENT" ||
                            orderDetails?.reportStatus === "SENT" ||
                            orderDetails?.report_status === "SENT" ||
                            orderDetails?.status === "REPORT_SENT"
                              ? "Sent"
                              : "Not Sent"}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <div className={`row mb-3 pb-1 ${styles["details-grid"]} `}>
                  <div className={`col-lg-6 ${styles["details-grid-leftCol"]}`}>
                    <div className={styles["white-card"]}>
                      <div className={styles["patient-tab-title"]}>
                        <h2>Case Note</h2>
                      </div>

                      <div className={styles["grid-col-4"]}>
                        <div className={styles["field-block"]}>
                          <div
                            className={
                              styles["value"] +
                              " " +
                              styles["font-medium"] +
                              " " +
                              styles["address-box"]
                            }
                          >
                            {orderDetails.caseNotes ||
                              "No case notes available"}
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className={`${styles["white-card"]} mb-0`}>
                      <div className={styles["patient-tab-title"]}>
                        <h2>Patient Address</h2>
                      </div>

                      <div className={styles["grid-col-4"]}>
                        <div
                          className={`${styles["field-block"]} ${styles["address-field"]}`}
                        >
                          <div
                            className={
                              styles["value"] +
                              " " +
                              styles["font-medium"] +
                              " " +
                              styles["address-box"]
                            }
                          >
                            <LiaMapMarkerSolid />
                            {orderDetails?.patientAddress ? (
                              <>
                                {orderDetails.patientAddress.houseFlatFloorNo ||
                                  "N/A"}
                                ,
                                <br />
                                {orderDetails.patientAddress.street || "N/A"},
                                <br />
                                {orderDetails.patientAddress.areaDistrict ||
                                  "N/A"}
                                ,
                                <br />
                                {orderDetails.patientAddress.city ||
                                  "N/A"},{" "}
                                {orderDetails.patientAddress.governorate ||
                                  "N/A"}
                                <br />
                                {orderDetails.patientAddress.country ||
                                  "N/A"} -{" "}
                                {orderDetails.patientAddress.postalCode ||
                                  "N/A"}
                              </>
                            ) : (
                              <>N/A</>
                            )}
                          </div>
                          {(() => {
                            const addr = orderDetails?.patientAddress;
                            const hasCoords =
                              addr &&
                              addr.latitude != null &&
                              addr.latitude !== "" &&
                              addr.longitude != null &&
                              addr.longitude !== "" &&
                              !isNaN(Number(addr.latitude)) &&
                              !isNaN(Number(addr.longitude));
                            const textAddress = addr
                              ? [
                                  addr.houseFlatFloorNo,
                                  addr.street,
                                  addr.areaDistrict,
                                  addr.city,
                                  addr.governorate,
                                  addr.country,
                                ]
                                  .filter(Boolean)
                                  .join(", ")
                              : "";
                            const destination = hasCoords
                              ? `${addr.latitude},${addr.longitude}`
                              : textAddress;
                            const mapsUrl = destination
                              ? `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(destination)}&travelmode=driving&dir_action=navigate`
                              : null;
                            return mapsUrl ? (
                              <Link href={mapsUrl} target="_blank">
                                Open in Maps
                              </Link>
                            ) : (
                              <span style={{ color: "#89899b" }}>
                                Open in Maps
                              </span>
                            );
                          })()}
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="col-lg-6">
                    <div
                      className={`${styles["white-card"]} ${styles["prescription-card"]}`}
                    >
                      <div className={styles["patient-tab-title"]}>
                        <h2>Prescription</h2>
                      </div>
                      <div>
                        <div className={styles["medical-history"]}>
                          {(() => {
                            const prescriptionDocs =
                              orderDetails?.documents?.filter(
                                (doc) => doc.document_type === "PRESCRIPTION",
                              ) || [];

                            if (prescriptionDocs.length > 0) {
                              return prescriptionDocs.map((doc, index) => {
                                const docUrl =
                                  doc.presignedUrl || doc.presigned_url;
                                // A presigned URL is generated even for a missing S3 object, so rely on
                                // the backend's file_available HEAD check to decide whether the file is
                                // actually there.
                                const fileAvailable =
                                  doc.file_available !== false && !!docUrl;
                                return (
                                  <div
                                    key={doc.id || index}
                                    className={styles["upload-row"]}
                                  >
                                    <div
                                      className={
                                        styles["upload-item"] +
                                        " " +
                                        styles["w-100"]
                                      }
                                    >
                                      <div className={styles["file-name"]}>
                                        <AiOutlineFilePdf
                                          size={30}
                                          className="text-danger"
                                        />
                                        <span className="d-flex flex-column">
                                          <strong>
                                            {doc.file_name ||
                                              "Prescription Document"}
                                          </strong>
                                          <span>
                                            Uploaded:{" "}
                                            {doc.uploaded_at
                                              ? tzCustom(new Date(
                                                  doc.uploaded_at,
                                                ), {
                                                  month: "short",
                                                  day: "numeric",
                                                  year: "numeric",
                                                }) +
                                                " at " +
                                                tzCustom(new Date(
                                                  doc.uploaded_at,
                                                ), {
                                                  hour: "numeric",
                                                  minute: "2-digit",
                                                  hour12: true,
                                                })
                                              : "N/A"}
                                          </span>
                                        </span>
                                      </div>
                                      {fileAvailable && (
                                        <a
                                          href={docUrl}
                                          target="_blank"
                                          rel="noopener noreferrer"
                                          style={{
                                            cursor: "pointer",
                                            textDecoration: "none",
                                            color: "inherit",
                                          }}
                                        >
                                          <RxChevronDown
                                            size={24}
                                            className="text-secondary"
                                          />
                                        </a>
                                      )}
                                    </div>
                                  </div>
                                );
                              });
                            } else {
                              return (
                                <div className="text-center py-4">
                                  <AiOutlineFilePdf
                                    size={48}
                                    className="text-muted mb-2"
                                  />
                                  <p className="text-muted mb-0">
                                    No prescription found
                                  </p>
                                  <small className="text-muted">
                                    No prescription has been uploaded for this
                                    order
                                  </small>
                                </div>
                              );
                            }
                          })()}

                          {(() => {
                            const prescriptionDocs =
                              orderDetails?.documents?.filter(
                                (doc) => doc.document_type === "PRESCRIPTION",
                              ) || [];

                            if (prescriptionDocs.length > 0) {
                              return prescriptionDocs.map((doc, index) => {
                                const docUrl =
                                  doc.presignedUrl || doc.presigned_url;
                                const fileAvailable =
                                  doc.file_available !== false && !!docUrl;
                                if (fileAvailable) {
                                  return (
                                    <div
                                      key={doc.id || index}
                                      className={styles["preview-scroller"]}
                                    >
                                      <div
                                        className={styles["uploaded-preview"]}
                                      >
                                        <div
                                          className={styles["preview-wrapper"]}
                                        >
                                          <img
                                            src={reportPreview}
                                            alt="Report Preview"
                                          />
                                        </div>
                                      </div>
                                    </div>
                                  );
                                }
                                return (
                                  <div
                                    key={doc.id || index}
                                    style={{
                                      width: "100%",
                                      boxSizing: "border-box",
                                      border: "1px dashed #FCA5A5",
                                      borderRadius: "8px",
                                      backgroundColor: "#FEF2F2",
                                      padding: "32px 16px",
                                      display: "flex",
                                      flexDirection: "column",
                                      alignItems: "center",
                                      justifyContent: "center",
                                      textAlign: "center",
                                      gap: "6px",
                                      marginTop: "8px",
                                    }}
                                  >
                                    <AiOutlineFilePdf
                                      size={34}
                                      style={{ color: "#B91C1C", opacity: 0.6 }}
                                    />
                                    <div
                                      style={{
                                        fontSize: 14,
                                        fontWeight: 600,
                                        color: "#B91C1C",
                                      }}
                                    >
                                      Prescription not uploaded properly
                                    </div>
                                    <div
                                      style={{
                                        fontSize: 12,
                                        color: "#6b7280",
                                        maxWidth: 360,
                                        lineHeight: 1.5,
                                      }}
                                    >
                                      We couldn't load this prescription. It may
                                      not have been uploaded correctly or the
                                      file is no longer available. Please ask
                                      the patient to re-upload it.
                                    </div>
                                  </div>
                                );
                              });
                            }
                            return null;
                          })()}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* test details  */}
                <div className={styles["white-card"]}>
                  <div className={styles["patient-tab-title"]}>
                    <h2>Test Details</h2>
                  </div>
                  <div className={styles["bg-gray-light"] + " p-2"}>
                    <div
                      className={`${styles["apt-appointments-table"]} ${styles["test-details-table"]}`}
                    >
                      <table>
                        <thead>
                          <tr>
                            <th>Test Name</th>
                            <th>Location</th>
                            <th>Price</th>
                          </tr>
                        </thead>

                        <tbody>
                          {loading ? (
                            <tr>
                              <td colSpan={3}>
                                <Loader />
                              </td>
                            </tr>
                          ) : (
                            <>
                              {tests.length === 0 ? (
                                <tr>
                                  <td
                                    colSpan={3}
                                    style={{
                                      textAlign: "center",
                                      padding: "20px",
                                      color: "#6b7280",
                                    }}
                                  >
                                    No tests available
                                  </td>
                                </tr>
                              ) : (
                                <>
                                  {tests
                                    .filter(
                                      (test) =>
                                        test && test.testName && test.price,
                                    )
                                    .map((item, idx) => (
                                      <tr key={idx}>
                                        <td>{item.testName || "N/A"}</td>
                                        <td>
                                          <span
                                            className={`${styles["db-status"]} ${
                                              styles[
                                                getStatusColor(item.location)
                                              ]
                                            }`}
                                          >
                                            {item.location === "HOME_VISIT"
                                              ? "Home Visit"
                                              : "On-Site"}
                                          </span>
                                        </td>
                                        <td>
                                          IQD{" "}
                                          {parsePrice(
                                            item.price,
                                          ).toLocaleString("en-US", {
                                            minimumFractionDigits: 0,
                                            maximumFractionDigits: 0,
                                          })}
                                        </td>
                                      </tr>
                                    ))}
                                </>
                              )}
                            </>
                          )}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>

                <div className={styles["white-card"]}>
                  <div className={styles["patient-tab-title"]}>
                    <h2>Invoice</h2>
                    {/* <Link href=""><FaCirclePlus /> Add Test</Link> */}
                  </div>

                  {tests && tests.length > 0 ? (
                    <>
                      {tests
                        .filter((test) => test && test.testName && test.price)
                        .map((test, index) => (
                          <div
                            key={test.id || index}
                            className={styles["test-row"]}
                          >
                            <span className={styles["test-label"]}>
                              {test.testName || "Test"}
                            </span>
                            <span className={styles["test-value-strong"]}>
                              IQD{" "}
                              {formatWholeAmount(parsePrice(test.price))}
                            </span>
                          </div>
                        ))}

                      {/* Calculate totals */}
                      {(() => {
                        // Use backend invoice data if available, otherwise calculate locally
                        const backendInvoice = orderDetails?.invoice;

                        let subtotal,
                          transportationFees,
                          adminFee,
                          total,
                          adminFeePercentage;

                        // Loyalty points the patient redeemed, persisted on the booking
                        // and returned on the order and its `invoice` block. Shown as a
                        // deduction so this breakdown's Total matches the printed invoice
                        // and what was actually charged.
                        const loyaltyDiscount = Number(
                          backendInvoice?.loyalty_discount ??
                            orderDetails?.loyaltyDiscount ??
                            0,
                        );
                        const loyaltyPointsUsed = Number(
                          backendInvoice?.loyalty_points_used ??
                            orderDetails?.loyaltyPointsUsed ??
                            0,
                        );
                        // Promo code discount and (INSURANCE-method payments only) the
                        // insurance deduction applied at checkout, from the gateway's
                        // order-details enrichment — each gets its own row below.
                        const promoDiscount = Number(
                          backendInvoice?.promo_discount || 0,
                        );
                        const promoCode = backendInvoice?.promo_code || null;
                        const insuranceCoverage = Number(
                          backendInvoice?.insurance_coverage || 0,
                        );

                        if (backendInvoice) {
                          // Use backend calculated values
                          subtotal = backendInvoice.subtotal || 0;
                          transportationFees = backendInvoice.transportation_fee || 0;
                          adminFee = backendInvoice.admin_fee || 0;
                          // The backend total is gross. The patient's Total is DERIVED
                          // from it and the deduction rows below (as on LabInvoiceModal
                          // and the doctor invoice), not taken from the payment's
                          // `amount_paid`, which is net of a promo / insurance deduction
                          // that had no row.
                          total = Math.max(
                            0,
                            Number(backendInvoice.total || 0) -
                              insuranceCoverage -
                              loyaltyDiscount -
                              promoDiscount,
                          );
                          // Persisted fee snapshot, else the LIVE admin panel
                          // configuration. Never a hardcoded rate. `??` so a legitimate 0
                          // snapshot (no admin fee) is respected.
                          adminFeePercentage = Number(
                            backendInvoice.admin_fee_percentage ??
                              configAdminFeePercentage ??
                              0,
                          );
                        } else {
                          // Calculate locally
                          const validTests = tests.filter(
                            (test) => test && test.testName && test.price,
                          );

                          subtotal = validTests.reduce((sum, test) => {
                            const price = parsePrice(test.price);
                            return sum + price;
                          }, 0);

                          transportationFees = validTests.reduce((sum, test) => {
                            const transportationFee = parsePrice(test.transportationFee);
                            return sum + transportationFee;
                          }, 0);

                          // Persisted fee snapshot when the order has one, else the LIVE
                          // admin panel configuration. Never a hardcoded rate.
                          adminFeePercentage = Number(
                            orderDetails?.invoice?.admin_fee_percentage ??
                              configAdminFeePercentage ??
                              0,
                          );
                          // Admin fee is calculated on (subtotal + transportation fee), matching the backend invoice.
                          adminFee =
                            (subtotal + transportationFees) *
                            (adminFeePercentage / 100);
                          total = Math.max(
                            0,
                            subtotal +
                              transportationFees +
                              adminFee -
                              loyaltyDiscount,
                          );
                        }

                        return (
                          <>
                            <hr
                              style={{
                                margin: "16px 0",
                                border: "none",
                                borderTop: "1px solid #eee",
                              }}
                            />

                            <div className={styles["test-row"]}>
                              <span className={styles["test-label"]}>
                                Subtotal
                              </span>
                              <span className={styles["test-value-strong"]}>
                                IQD{" "}
                                {subtotal.toLocaleString("en-US", {
                                  minimumFractionDigits: 0,
                                  maximumFractionDigits: 0,
                                })}
                              </span>
                            </div>

                            {transportationFees > 0 && (
                              <div className={styles["test-row"]}>
                                <span className={styles["test-label"]}>
                                  Transportation Fees
                                </span>
                                <span className={styles["test-value-strong"]}>
                                  IQD{" "}
                                  {transportationFees.toLocaleString("en-US", {
                                    minimumFractionDigits: 0,
                                    maximumFractionDigits: 0,
                                  })}
                                </span>
                              </div>
                            )}

                            {/* Admin fee row is rendered only when one actually applies —
                                no admin fee configured in the admin panel means no row
                                (and no charge), matching the appointment-detail page. */}
                            {Number(adminFee) > 0 && (
                              <div className={styles["test-row"]}>
                                <span className={styles["test-label"]}>
                                  {t(
                                    "lab.appointmentDetail.adminFee",
                                    "Admin Fee",
                                  )}{" "}
                                  ({formatPercent(adminFeePercentage || 0)}
                                  %)
                                </span>
                                <span className={styles["test-value-strong"]}>
                                  IQD{" "}
                                  {adminFee.toLocaleString("en-US", {
                                    minimumFractionDigits: 0,
                                    maximumFractionDigits: 0,
                                  })}
                                </span>
                              </div>
                            )}

                            {insuranceCoverage > 0 && (
                              <div className={styles["test-row"]}>
                                <span className={styles["test-label"]}>
                                  {t(
                                    "lab.appointmentDetail.insuranceCoverage",
                                    "Insurance Coverage",
                                  )}
                                </span>
                                <span
                                  className={styles["test-value-strong"]}
                                  style={{ color: "#00BC7D" }}
                                >
                                  − IQD{" "}
                                  {insuranceCoverage.toLocaleString("en-US", {
                                    minimumFractionDigits: 0,
                                    maximumFractionDigits: 0,
                                  })}
                                </span>
                              </div>
                            )}

                            {/* Loyalty points redeemed — deduction (green, like the printed
                                invoice) so the Total reconciles with what was charged. */}
                            {loyaltyDiscount > 0 && (
                              <div className={styles["test-row"]}>
                                <span className={styles["test-label"]}>
                                  {t(
                                    "lab.appointmentDetail.loyaltyDiscount",
                                    "Loyalty Point Discount",
                                  )}
                                  {loyaltyPointsUsed > 0
                                    ? ` (${loyaltyPointsUsed} ${t("lab.appointmentDetail.points", "pts")})`
                                    : ""}
                                </span>
                                <span
                                  className={styles["test-value-strong"]}
                                  style={{ color: "#00BC7D" }}
                                >
                                  − IQD{" "}
                                  {loyaltyDiscount.toLocaleString("en-US", {
                                    minimumFractionDigits: 0,
                                    maximumFractionDigits: 0,
                                  })}
                                </span>
                              </div>
                            )}

                            {promoDiscount > 0 && (
                              <div className={styles["test-row"]}>
                                <span className={styles["test-label"]}>
                                  {t(
                                    "lab.appointmentDetail.promoDiscount",
                                    "Promo Discount",
                                  )}
                                  {promoCode ? ` (${promoCode})` : ""}
                                </span>
                                <span
                                  className={styles["test-value-strong"]}
                                  style={{ color: "#00BC7D" }}
                                >
                                  − IQD{" "}
                                  {promoDiscount.toLocaleString("en-US", {
                                    minimumFractionDigits: 0,
                                    maximumFractionDigits: 0,
                                  })}
                                </span>
                              </div>
                            )}

                            <div
                              className={
                                styles["test-row"] +
                                " " +
                                styles["total-price-row"]
                              }
                            >
                              <span className={styles["test-value-strong"]}>
                                Total Price
                              </span>
                              <span className={styles["test-value-strong"]}>
                                IQD{" "}
                                {total.toLocaleString("en-US", {
                                  minimumFractionDigits: 0,
                                  maximumFractionDigits: 0,
                                })}
                              </span>
                            </div>
                          </>
                        );
                      })()}
                    </>
                  ) : (
                    <div className={styles["test-row"]}>
                      <span className={styles["test-label"]}>
                        No tests available
                      </span>
                      <span className={styles["test-value-strong"]}>IQD 0</span>
                    </div>
                  )}
                </div>
              </>
            ) : (
              <div className="text-center py-5">
                <div className="alert alert-warning">
                  <h4>No Report Data</h4>
                  <p>No report details available for this ID.</p>
                  <button
                    className="btn btn-secondary mt-2"
                    onClick={() => navigate(-1)}
                  >
                    Go Back
                  </button>
                </div>
              </div>
            )}
          </div>
        )}

        {/* {activeTab === "Technician Detail" && (
            <div className={`${styles["patient-tab-content"]} p-0 bg-white`}>
              <div className={styles["white-card"]}>
                <div className={`${styles["patient-tab-title"]} ${styles["med-tab-title"]}`}>
                  <h2>Schedule & Dispatch</h2>
                  <div className={styles["patient-actions-wrap"]}></div>
                </div>

                <div className={styles["medical-history"]}>
                  <div className={`${styles["history-grid"]} ${styles["med-grid"]} border-0`} >
                    <div className={styles["field-block"]}>
                      <div className={styles["label"]}>Service Date</div>
                      <div className={styles["value"]}>
                        24-12-2025
                      </div>
                    </div>
                    
  
                    <div className={styles["field-block"]}>
                      <div className={styles["label"]}>Service Time</div>
                      <div className={styles["value"]}>
                        Morning, 10:00 AM
                      </div>
                    </div>
                  </div>

                    <div className={`${styles["max-w-450"]}`} >
                      <div className={`${styles["label"]} mb-1`}>Assign Lab Technician </div>
                      <div className={styles["value"]}>
                        <select className={styles["test-select"]}>
                          <option value="">Select lab technician</option>
                          <option value="technician1">Technician 1</option>
                          <option value="technician2">Technician 2</option>
                        </select>
                      </div>
                    </div>

                    <hr />

                    <div className={`${styles["patient-tab-title"]} ${styles["med-tab-title"]} border-0`}>
                      <h2>Technician Status</h2>
                    </div>
                    <button className={`${styles["apt-register-btn"]} ${styles["text-green-info"]} py-2 px-3`}><Check size={16} /> Dispatched</button>

                    <hr />

                    <div className={`${styles["patient-tab-title"]} ${styles["med-tab-title"]} border-0`}>
                      <h2>Mark as Collected</h2>
                    </div>
                    <button className={`${styles["apt-register-btn"]}  py-2 px-3`}><Check size={16} /> Mark as Collected</button>

                    <hr />

                    <div className={`${styles["patient-tab-title"]} ${styles["med-tab-title"]} border-0`}>
                      <h2>Payment Collection</h2>
                    </div>
                    <button className={`${styles["apt-register-btn"]} ${styles["gray_colored"]} py-2 px-3`}><Check size={16} /> Mark as Collected</button>
                      
                    <hr />

                    <div className={`${styles["patient-tab-title"]} ${styles["med-tab-title"]} border-0`}>
                      <h2>Complete Order</h2>
                    </div>
                    <button className={`${styles["apt-register-btn"]} ${styles["gray_colored"]} py-2 px-3`}><Check size={16} /> Mark as Completed</button>
                      

                

                </div>

                  <div className={`${styles["text-end"]} mt-4`}>
                    <button className={styles["green-btn"]}><FaLocationArrow /> Send Patient Details via WhatsApp</button  >
                  </div>
                </div>
                    



                              

            

            </div>
          )} */}

        {activeTab === "Results & Reports" && (
          <>
            <div className={`${styles["patient-tab-content"]} p-0`}>
              <div className={`${styles["white-card"]} m-0 position-relative`}>
                {/* Check if report is already sent */}
                {orderDetails?.reportStatus === "SENT" && (
                  <div
                    className={styles["report-sent-notice"]}
                    style={{
                      backgroundColor: "#e8f5e8",
                      border: "1px solid #4caf50",
                      borderRadius: "8px",
                      padding: "12px 16px",
                      marginBottom: "16px",
                      color: "#2e7d32",
                      fontSize: "14px",
                      fontWeight: "500",
                    }}
                  >
                    <Check
                      size={16}
                      style={{ marginRight: "8px", verticalAlign: "middle" }}
                    />
                    Report has been sent to patient. Upload, sharing, and file
                    deletion are disabled.
                  </div>
                )}

                <input
                  type="file"
                  id="file-upload"
                  className={styles["file-input"]}
                  accept=".pdf"
                  multiple
                  onChange={handleMultipleFileUpload}
                  disabled={
                    uploadingFile || orderDetails?.reportStatus === "SENT"
                  }
                />
                <label
                  className={`${styles["result-upload-card"]} ${uploadingFile ? styles["uploading"] : ""} ${orderDetails?.reportStatus === "SENT" ? styles["disabled"] : ""}`}
                  htmlFor="file-upload"
                  style={{
                    opacity: orderDetails?.reportStatus === "SENT" ? 0.6 : 1,
                    cursor:
                      orderDetails?.reportStatus === "SENT"
                        ? "not-allowed"
                        : "pointer",
                    pointerEvents:
                      orderDetails?.reportStatus === "SENT" ? "none" : "auto",
                  }}
                >
                  <h2>Upload Test Results</h2>
                  <p className={styles["result-subtitle"]}>
                    {orderDetails?.reportStatus === "SENT"
                      ? "Report has been sent to patient. Upload is disabled."
                      : "Upload PDF reports (single or multiple files, max 10 files, 25MB each). All files will be automatically available in the patient's app."}
                  </p>

                  <span className={styles["result-upload-btn"]}>
                    <Upload size={20} />{" "}
                    {orderDetails?.reportStatus === "SENT"
                      ? "Upload Disabled"
                      : uploadingFile
                        ? "Uploading..."
                        : "Upload"}
                  </span>
                </label>

                <div className={`${styles["result-file-list"]} mt-4`}>
                  {/* Show uploaded files from order details */}
                  {orderDetails?.documents
                    ?.filter((doc) => doc.document_type === "TEST_RESULT")
                    .map((file, index) => (
                      <div
                        key={file.id || index}
                        className={styles["result-file-item"]}
                      >
                        <span className={styles["result-file-icon"]}>
                          <AiOutlineFilePdf size={20} color="#E55353" />
                        </span>
                        <span
                          className={`${styles["result-file-name"]} ${styles["clickable-file"]}`}
                          onClick={() =>
                            handleFileClick(file.id, file.file_name)
                          }
                          style={{
                            cursor: "pointer",
                            color: "#155DFC",
                            textDecoration: "underline",
                          }}
                        >
                          {file.file_name || `Test Result ${index + 1}.pdf`}
                        </span>
                        <button
                          className={styles["result-delete-btn"]}
                          onClick={() =>
                            handleFileDelete(file.id, file.file_name)
                          }
                          disabled={orderDetails?.reportStatus === "SENT"}
                          style={{
                            opacity:
                              orderDetails?.reportStatus === "SENT" ? 0.4 : 1,
                            cursor:
                              orderDetails?.reportStatus === "SENT"
                                ? "not-allowed"
                                : "pointer",
                            backgroundColor:
                              orderDetails?.reportStatus === "SENT"
                                ? "#f5f5f5"
                                : "",
                          }}
                        >
                          <Trash2 size={18} />
                        </button>
                      </div>
                    ))}

                  {/* Show uploaded files from current session */}
                  {uploadedFiles.map((file, index) => (
                    <div
                      key={file.id || `uploaded-${index}`}
                      className={styles["result-file-item"]}
                    >
                      <span className={styles["result-file-icon"]}>
                        <AiOutlineFilePdf size={20} color="#E55353" />
                      </span>
                      <span
                        className={`${styles["result-file-name"]} ${styles["clickable-file"]}`}
                        onClick={() => handleFileClick(file.id, file.fileName)}
                        style={{
                          cursor: "pointer",
                          color: "#155DFC",
                          textDecoration: "underline",
                        }}
                      >
                        {file.fileName}
                      </span>
                      <span className={styles["result-file-time"]}>
                        {tzDateTimeNumeric(new Date(file.uploadedAt))}
                      </span>
                      <button
                        className={styles["result-delete-btn"]}
                        onClick={() => {
                          // Remove from uploaded files list
                          setUploadedFiles((prev) =>
                            prev.filter((f) => f.id !== file.id),
                          );
                          toast.info("File removed from list");
                        }}
                        disabled={orderDetails?.reportStatus === "SENT"}
                        style={{
                          opacity:
                            orderDetails?.reportStatus === "SENT" ? 0.4 : 1,
                          cursor:
                            orderDetails?.reportStatus === "SENT"
                              ? "not-allowed"
                              : "pointer",
                          backgroundColor:
                            orderDetails?.reportStatus === "SENT"
                              ? "#f5f5f5"
                              : "",
                        }}
                      >
                        <Trash2 size={18} />
                      </button>
                    </div>
                  ))}

                  {/* Show message if no files uploaded */}
                  {(!orderDetails?.documents ||
                    orderDetails.documents.filter(
                      (doc) => doc.document_type === "TEST_RESULT",
                    ).length === 0) &&
                    uploadedFiles.length === 0 && (
                      <div className={styles["no-files-message"]}>
                        <p>No test results uploaded yet.</p>
                      </div>
                    )}
                </div>

                <div className={styles["result-actions"]}>
                  <button
                    className={styles["result-share-btn"]}
                    onClick={handleShareWithPatient}
                    disabled={orderDetails?.reportStatus === "SENT"}
                    style={{
                      opacity: orderDetails?.reportStatus === "SENT" ? 0.6 : 1,
                      cursor:
                        orderDetails?.reportStatus === "SENT"
                          ? "not-allowed"
                          : "pointer",
                      backgroundColor:
                        orderDetails?.reportStatus === "SENT" ? "#ccc" : "",
                    }}
                  >
                    <Check size={16} />{" "}
                    {orderDetails?.reportStatus === "SENT"
                      ? "Already Shared"
                      : "Share with Patient & Doctor"}
                  </button>
                </div>
              </div>
            </div>
          </>
        )}
      </div>{" "}
    </LabDashboardLayout>
  );
};

export default LabManagementPatientAppointmentDetail;
